import firebase from "firebase/app";
import "firebase/messaging";
import { fetchPushToken } from "../actions/login";
import {
	CLEAR_REGISTER_PUSH_NOTIFICATIONS,
	SET_DROP_DOWN_ALERT_INFO
} from "../actions/types";
import {
	canRegisterSilently,
	isPushSupported,
	needsInstallForPush,
	PERMISSION_GRANTED,
	requestPermission
} from "../utils/WebPush";

/**
 * FCM registration for the web build, shared by the automatic path (permission
 * already granted) and the gesture-driven path (the opt-in banner and the
 * settings row).
 */

/** Scope the SDK registers `/firebase-messaging-sw.js` under. */
const SW_SCOPE = "/firebase-cloud-messaging-push-scope";

let messagingInstance;

/**
 * `firebase.messaging()` throws on browsers without the Push APIs, so it is
 * never called before `isSupported()` has cleared it.
 */
export const getMessaging = () => {
	if (!isPushSupported() || !firebase.messaging.isSupported()) {
		return null;
	}
	if (!messagingInstance) {
		try {
			messagingInstance = firebase.messaging();
		} catch (error) {
			console.log("Firebase messaging is unavailable", error);
			return null;
		}
	}
	return messagingInstance;
};

export const isWebPushAvailable = () =>
	Boolean(getMessaging()) && !needsInstallForPush();

/**
 * The SDK's service worker is registered under its own narrow scope and does
 * not control the page, so `navigator.serviceWorker.ready` — which waits for
 * the registration controlling this document — would never settle here.
 */
export const getPushServiceWorkerRegistration = async () => {
	if (!isPushSupported()) {
		return null;
	}
	return (
		(await navigator.serviceWorker.getRegistration(SW_SCOPE)) ??
		(await navigator.serviceWorker.getRegistration()) ??
		null
	);
};

/**
 * Fetches the FCM token. The permission must already be granted: `getToken()`
 * prompts on its own when it is not, and that prompt is rejected by iOS because
 * the call no longer belongs to the gesture that started it.
 *
 * The service worker is left to the SDK, which registers
 * `/firebase-messaging-sw.js` under its own scope.
 */
const fetchToken = async () => {
	const messaging = getMessaging();
	if (!messaging || !canRegisterSilently()) {
		return null;
	}
	try {
		return await messaging.getToken();
	} catch (error) {
		console.log("An error occurred while retrieving the push token", error);
		return null;
	}
};

/**
 * Registers the device and hands the token to the server.
 *
 * `promptIfNeeded` may only be true when this runs inside a user gesture; it is
 * what the banner and the settings row pass, while the automatic path leaves it
 * false and gives up silently when the permission has not been granted yet.
 *
 * Returns the resulting permission so callers can refresh their own UI.
 */
export const registerWebPush = async (
	dispatch,
	lang,
	{ promptIfNeeded = false } = {}
) => {
	if (!isWebPushAvailable()) {
		return null;
	}

	let permission = Notification.permission;
	if (permission !== PERMISSION_GRANTED && promptIfNeeded) {
		permission = await requestPermission();
	}
	if (permission !== PERMISSION_GRANTED) {
		// Only tell the user when they just answered the prompt: the automatic
		// path runs on every login and must stay silent.
		if (promptIfNeeded) {
			dispatch({
				type: SET_DROP_DOWN_ALERT_INFO,
				info: "notifinfo"
			});
		}
		dispatch({ type: CLEAR_REGISTER_PUSH_NOTIFICATIONS });
		return permission;
	}

	const token = await fetchToken();
	if (token) {
		dispatch(fetchPushToken(token, lang));
	} else {
		dispatch({
			type: SET_DROP_DOWN_ALERT_INFO,
			info: "notifinfo"
		});
		dispatch({ type: CLEAR_REGISTER_PUSH_NOTIFICATIONS });
	}

	return permission;
};
